import { Alert, Box, Button, Icon, Typography } from "@outschool/backpack";
import { CurrentSubscriptionStatus } from "@outschool/gql-frontend-generated";
import { faArrowUpRightFromSquare } from "@outschool/icons";
import { Trans, useTranslation } from "@outschool/localization";
import { IntegrationCategory, useTrackEvent } from "@outschool/ui-analytics";
import { ExternalLink } from "@outschool/ui-components-shared";
import { Modal } from "@outschool/ui-legacy-component-library";
import React from "react";
import { useLocation } from "react-router-dom";

import * as Env from "../../../shared/Env";
import {
  useCreateSetupIntentWithPaymentElement,
  usePaymentMethods,
} from "../../hooks/CurrentUser";
import { useSubscriptionCreditBalance } from "../../hooks/useSubscriptionCreditBalance";
import { useStripeCheckout } from "../../routes/Subscriptions/hooks/useStripeCheckout";
import ErrorMessage from "../ErrorMessage";
import LoadingWithMessage from "../LoadingWithMessage";
import {
  BillingDetails,
  StripeProvider,
  useDefaultStripeErrorMessage,
} from "../payments/Stripe";
import {
  STRIPE_PRIVACY_POLICY_URL,
  STRIPE_TOS_URL,
} from "../payments/StripeAuthorization";
import StripeNewCard from "../payments/StripeNewCard";
import { StripeRemoveSavedCard } from "../payments/StripeSavedCards";

type PaymentMethodFormOptions = {
  title?: string;
  description?: string;
  buttonLabel?: string;
  savePaymentMethodsLoadingText?: string;
};
type PaymentMethodModalProps = {
  open: boolean;
};
type PaymentMethodModalContentProps = {
  onClose?: () => void;
  onPaymentSaved?: () => Promise<void>;
  onGoBack?: () => void;
  formOptions?: PaymentMethodFormOptions;
  closeAfterSave?: boolean;
  isCoppaConsentCheck?: boolean;
};

/**
 * This is a consumer for <StripeProvider /> thus the nested components
 */
function SavePaymentMethodModalContent({
  onClose = () => {},
  onPaymentSaved = async () => {},
  onGoBack,
  formOptions = {},
  closeAfterSave = true,
  isCoppaConsentCheck = false,
}: PaymentMethodModalContentProps) {
  const { t } = useTranslation(
    "client\\components\\account\\SavePaymentMethod",
  );
  const [isComplete, setIsComplete] = React.useState(false);
  const errorMessage = useDefaultStripeErrorMessage();

  const {
    title = null,
    description = null,
    buttonLabel = t`Save Card`,
    savePaymentMethodsLoadingText = t`Please wait while we save your credit card`,
  } = formOptions;

  const {
    loading: paymentMethodsLoading,
    error: paymentMethodsError,
    paymentMethods,
  } = usePaymentMethods();
  const [defaultPaymentMethod] = paymentMethods;

  const [createSetupIntentWithPaymentElement, { error, loading }] =
    useCreateSetupIntentWithPaymentElement(defaultPaymentMethod?.uid);

  const { currentStatus: currentMembershipStatus, loading: membershipLoading } =
    useSubscriptionCreditBalance();
  const { createBillingSessionAndRedirect } = useStripeCheckout();
  const location = useLocation();
  const trackEvent = useTrackEvent(IntegrationCategory.Functional);

  const hasDefaultPaymentMethod = !!defaultPaymentMethod;
  React.useEffect(() => {
    if (!paymentMethodsLoading && !membershipLoading) {
      trackEvent("save_payment_method_modal_view", {
        membershipStatus: currentMembershipStatus,
        hasDefaultPaymentMethod,
      });
    }
  }, [
    trackEvent,
    currentMembershipStatus,
    paymentMethodsLoading,
    membershipLoading,
    hasDefaultPaymentMethod,
  ]);

  const handleSaveCard = React.useCallback(async () => {
    await createSetupIntentWithPaymentElement();
    await onPaymentSaved();
    if (closeAfterSave) {
      onClose();
    }
  }, [
    createSetupIntentWithPaymentElement,
    onClose,
    onPaymentSaved,
    closeAfterSave,
  ]);

  const handleGoBack = React.useCallback(() => {
    if (onGoBack) {
      // for callers that want to have a different behavior than "close" (e.g.
      // go back to a different modal), provide `onGoBack`
      onGoBack();
    } else {
      onClose();
    }
  }, [onGoBack, onClose]);

  return (
    <Box>
      <Typography
        variant="h3"
        sx={{
          paddingBottom: "0.5em",
        }}
      >
        {title
          ? title
          : paymentMethodsLoading || paymentMethodsError
          ? t`Manage payment methods`
          : defaultPaymentMethod
          ? t`Default payment method`
          : t`Save a credit card for later`}
      </Typography>
      {isCoppaConsentCheck && (
        <Box
          sx={{
            marginTop: "1em",
            marginBottom: "1em",
          }}
        >
          {/* eslint-disable react/no-unescaped-entities */}
          <Trans t={t}>
            <span>
              We collect your credit card info to make sure you're a grown-up
              (not a kid) and to comply with a U.S. law called{" "}
              <ExternalLink url="https://www.ftc.gov/tips-advice/business-center/guidance/complying-coppa-frequently-asked-questions-0">
                The Children's Online Privacy Protection Act (COPPA).
              </ExternalLink>
            </span>
          </Trans>
          {/* eslint-enable react/no-unescaped-entities */}
        </Box>
      )}
      <Box
        sx={{
          paddingTop: "0.5em",
        }}
      >
        {paymentMethodsError ? (
          <ErrorMessage
            value={errorMessage}
            showStatusPageLink={Env.IS_READ_ONLY_MODE}
          />
        ) : paymentMethodsLoading ? (
          <LoadingWithMessage>
            {t`Please wait while we load your payment methods`}
          </LoadingWithMessage>
        ) : defaultPaymentMethod ? (
          <>
            {currentMembershipStatus === CurrentSubscriptionStatus.Active && (
              <Alert severity="info" sx={{ marginBottom: 16 }}>
                {t(
                  "You cannot remove your payment method with an active membership. ",
                )}
                <Button
                  variant="link"
                  // size="small"
                  onClick={(e: React.MouseEvent) => {
                    e.preventDefault();
                    createBillingSessionAndRedirect({
                      newTab: false,
                      onReturnPath: location.pathname,
                    });
                  }}
                  sx={{
                    marginTop: 16,
                  }}
                >
                  {t("Manage Plan")}
                  <Icon
                    icon={faArrowUpRightFromSquare}
                    sx={{ marginLeft: 4 }}
                  />
                </Button>
              </Alert>
            )}
            <StripeRemoveSavedCard
              defaultPaymentMethod={defaultPaymentMethod}
              disabled={
                paymentMethodsLoading ||
                currentMembershipStatus === CurrentSubscriptionStatus.Active
              }
            />
          </>
        ) : (
          <>
            <Alert severity="warning" sx={{ marginBottom: "16px" }}>
              <Box>
                <Trans t={t}>
                  <p>
                    This card will be used for all recurring charges, through
                    Stripe, Inc, in accordance with their{" "}
                    <ExternalLink url={STRIPE_TOS_URL}>
                      Terms of Services
                    </ExternalLink>{" "}
                    and{" "}
                    <ExternalLink url={STRIPE_PRIVACY_POLICY_URL}>
                      Privacy Policy
                    </ExternalLink>
                    . If you need different payment methods for each learner,
                    contact{" "}
                    <ExternalLink url="mailto:support@outschool.com">
                      support@outschool.com
                    </ExternalLink>
                    .
                  </p>
                </Trans>
              </Box>
            </Alert>
            <Box
              sx={{
                paddingBottom: "1em",
              }}
            >
              <StripeNewCard
                setIsCardComplete={setIsComplete}
                setBillingDetails={(_billingDetails: BillingDetails) => {}}
              />
            </Box>
            {!!description && (
              <Box
                sx={{
                  marginBottom: "1em",
                }}
              >
                {description}
              </Box>
            )}
            {loading ? (
              <LoadingWithMessage>
                {savePaymentMethodsLoadingText}
              </LoadingWithMessage>
            ) : (
              <>
                <ErrorMessage
                  value={error && error.message}
                  showStatusPageLink={Env.IS_READ_ONLY_MODE}
                />
                <Button
                  variant="contained"
                  disabled={!isComplete || loading}
                  onClick={handleSaveCard}
                  sx={{
                    width: "100%",
                  }}
                >
                  {buttonLabel}
                </Button>
              </>
            )}
          </>
        )}
      </Box>
      <Box
        sx={{
          marginBottom: "1em",
          marginTop: "1em",
          width: "100%",
        }}
      >
        <Button
          variant="link"
          onClick={handleGoBack}
          sx={{
            width: "100%",
            lineHeight: 1.5,
          }}
          disabled={loading}
        >
          {t("Go Back")}
        </Button>
      </Box>
    </Box>
  );
}

/**
 * Provides stripe when modal is loaded
 */
export function SavePaymentMethodModal({
  open,
  onClose,
  ...otherProps
}: PaymentMethodModalProps & PaymentMethodModalContentProps) {
  return (
    <Modal open={open} onClose={onClose}>
      <SavePaymentMethod onClose={onClose} {...otherProps} />
    </Modal>
  );
}

export function SavePaymentMethod(props: PaymentMethodModalContentProps) {
  return (
    <StripeProvider amountInCents={0}>
      <SavePaymentMethodModalContent {...props} />
    </StripeProvider>
  );
}
